"use client";

import { colors, fonts } from "@/stylingConstants";
import React, { ReactElement, useEffect, useRef } from 'react';
import { CloseXButtonBase } from '@/components/CloseXButton';

interface ModalProps {
    onExit: () => void;
    iconElement?: ReactElement;
    title: string;
    buttons?: ReactElement[];
    children?: React.ReactNode;
    text?:string;
    className?: string;
    hideCloseButton?: boolean;
    customStyles?: {
        container?: React.CSSProperties;
        content?: React.CSSProperties;
        divider?: React.CSSProperties;
        title?: React.CSSProperties;
        text?: React.CSSProperties;
        buttons?: React.CSSProperties;
    };
}

/**
 * Flexible modal wrapper, for usage example see UnsupportedModal.tsx.
 * @param onExit        Behaviour when corner cross is clicked
 * @param iconElement   Optional icon to display at the top
 * @param title         The header
 * @param buttons       Optional - An array of buttons to display - this is usually CustomButton instances
 * @param children      Other elements to display in the main middle section of the modal
 * @param text          The text display in the main middle section of the modal
 * @param className     The html class to apply to the root modal element
 * @param hideCloseButton Optional - If true, the close button (cross) will be hidden
 * @param customStyles   Optional - object containing overrides for various elements { container, divider, title, text }
 * @constructor
 */
function Modal({onExit, iconElement, title, buttons, children, text, className, hideCloseButton, customStyles}: Readonly<ModalProps>) {
    const modalRef = useRef<HTMLDialogElement>(null);
    const previouslyFocusedElement = useRef<HTMLElement | null>(null);
    const titleId = `modal-title-${Math.random().toString(36).substring(2, 11)}`;
    const descriptionId = text ? `modal-description-${Math.random().toString(36).substring(2, 11)}` : undefined;

    useEffect(() => {
        const modal = modalRef.current;
        if (!modal) return;
        previouslyFocusedElement.current = document.activeElement instanceof HTMLElement 
            ? document.activeElement 
            : null;

        // Lock body scroll
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        modal.showModal();

        // Focus the first focusable element inside the modal
        const focusableElements = modal.querySelectorAll(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        );
        
        const firstFocusable = focusableElements[0] as HTMLElement | undefined;
        if (firstFocusable) {
            setTimeout(() => {
                firstFocusable.focus();
            }, 0);
        }

        const handleCancel = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (hideCloseButton) {
                    event.preventDefault();
                    return;
                }

                onExit();
            }
        };

        const handleTabKey = (event: KeyboardEvent) => {
            if (event.key !== 'Tab') return;

            const currentFocusableElements = modal.querySelectorAll(
                'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
            );
            
            if (currentFocusableElements.length === 0) return;

            const firstElement = currentFocusableElements[0] as HTMLElement;
            const lastElement = Array.from(currentFocusableElements).at(-1) as HTMLElement;

            if (event.shiftKey && document.activeElement === firstElement) {
                lastElement.focus();
                event.preventDefault();
            } else if (!event.shiftKey && document.activeElement === lastElement) {
                firstElement.focus();
                event.preventDefault();
            }
        };

        const handleBackdropClick = (event: MouseEvent) => {
            if (event.target === modal && !hideCloseButton) {
                onExit();
            }
        };

        document.addEventListener('keydown', handleCancel);
        modal.addEventListener('keydown', handleTabKey);
        modal.addEventListener('click', handleBackdropClick);

        return () => {
            document.removeEventListener('keydown', handleCancel);
            modal.removeEventListener('keydown', handleTabKey);
            modal.removeEventListener('click', handleBackdropClick);
            modal.close();
            
            document.body.style.overflow = originalOverflow;
            if (previouslyFocusedElement.current) {
                previouslyFocusedElement.current.focus();
            }
        };
    }, [onExit, hideCloseButton]);

    return (
        <dialog
            ref={modalRef}
            aria-modal="true"
            aria-labelledby={titleId}
            aria-describedby={descriptionId}
            tabIndex={-1}
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                maxWidth: 'none',
                maxHeight: 'none',
                margin: 0,
                padding: 0,
                border: 'none',
                outline: 'none',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 2000,
            }}
            className={className ?? ''}
        >
            <div
                style={{
                    width:'400px',
                    ...customStyles?.container
                }}
            >
                <div
                    style={{
                        position: 'relative',
                        marginRight:'16px',
                        marginLeft:'16px',
                        backgroundColor: 'white',
                        padding: '16px 16px 8px 16px',
                        borderRadius: '5px',
                        textAlign: 'center',
                        ...customStyles?.content
                    }}
                >
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                    }}>
                        <div style={{ marginLeft: 'auto', marginRight: 0, padding: 0, minHeight: 24 }}>
                            {!hideCloseButton && <button
                                onClick={onExit}
                                aria-label="Close modal"
                                type="button"
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <CloseXButtonBase 
                                    className="modalCornerCrossClose" 
                                    height={24} 
                                    width={24} 
                                    fill="black"
                                    aria-hidden="true"
                                />
                            </button>}
                        </div>
                        <div style={{ marginLeft: 'auto', marginRight: 'auto' }}>{iconElement}</div>
                        <h2 id={titleId} style={{ margin: '8px 0', ...fonts.headerlg, ...customStyles?.title }}>{title}</h2>
                        <div style={{height:1,backgroundColor:colors.grey20,width:263,margin:'0 auto', ...customStyles?.divider}}/>
                        {text && <div id={descriptionId} style={{ padding: '8px 16px',...fonts.bodylg, ...customStyles?.text }} >{text}</div>}
                        {children}
                    </div>
                    <div style={{ width: '100%', padding: '24px 8px',display:'flex',flexDirection:'column', ...customStyles?.buttons}}>
                        {buttons}
                    </div>
                </div>
            </div>
        </dialog>
    );
}

export default Modal;
